import React, { useState } from 'react';
import styled from 'astroturf/react';

import routes from 'sitewide-js/core_pre_webpack/routes';
import { trackClick } from 'js/utils/trackClick';
import $image_url from 'js/utils/image_url';
import { Heading } from 'js/design_system/Heading';
import { Button } from 'js/design_system/Button';
import LockIcon from 'js/svg/colorless/circle_lock.svg';
import LightBulbOnIcon from 'js/svg/colorless/lightbulb_on_outline.svg';
import GroupIcon from 'js/svg/colorless/account_multiple_outline.svg';
import FinanceIcon from 'js/svg/colorless/chart_bar.svg';

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  width: 100%;
  background: var(--background-white, #ffffff);

  @include breakpoint(large) {
    min-height: calc(100vh - 196px);
  }
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  max-width: 1230px;

  @include breakpoint(large) {
    flex-direction: row;
  }
`;

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--spacing-md-40, 40px);

  padding: var(--spacing-sm-24, 24px) var(--spacing-sm-24, 24px)
    var(--spacing-md-48, 48px) var(--spacing-sm-24, 24px);

  @include breakpoint(medium) {
    padding: var(--spacing-sm-24, 24px) var(--spacing-lg-64, 64px)
      var(--spacing-md-48, 48px) var(--spacing-lg-64, 64px);
  }

  @include breakpoint(large) {
    justify-content: space-between;
    padding: var(--spacing-md-40, 40px);
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs-16, 16px);
`;

const Badge = styled.div`
  display: none;

  @include breakpoint(large) {
    display: flex;
    padding: var(--spacing-xs-4, 4px) var(--spacing-xs-8, 8px);
    justify-content: center;
    align-items: flex-start;
    gap: var(--spacing-xs-4, 4px);
    border-radius: var(--columns-corner-radius-radius-md, 8px);
    border: 1px solid var(--premium-gold);
    margin: 0 auto;
  }
`;

const BadgeTitle = styled.div`
  color: var(--premium-gold);
  font-family: var(--type-font-family-sans-serif, 'DM Sans');
  font-size: var(--type-body-sm-size, 14px);
  font-style: normal;
  font-weight: 400;
  line-height: var(--type-body-sm-line-height, 20px); /* 142.857% */
`;

const Title = styled(Heading).attrs({
  fontFamily: 'serif',
})`
  text-align: center;
  text-transform: none;
  font-style: normal;
  font-weight: 400;
  font-size: var(--type-serif-md-size, 32px);
  line-height: var(--type-serif-md-size, 32px); /* 100% */
  letter-spacing: var(--type-serif-md-letter-spacing, -1px);

  @include breakpoint(medium, large) {
    font-size: var(--type-serif-lg-size, 58px);
    line-height: var(--type-serif-lg-size, 58px); /* 100% */
    letter-spacing: var(--type-serif-lg-letter-spacing, -1.5px);
  }
`;

const Subtitle = styled.div`
  color: var(--text-secondary, #535366);
  text-align: center;

  font-family: var(--type-font-family-sans-serif, 'DM Sans');
  font-size: var(--type-body-sm-size, 14px);
  font-style: normal;
  font-weight: 400;
  line-height: var(--type-body-sm-line-height, 20px); /* 142.857% */

  @include breakpoint(medium) {
    font-size: var(--type-body-md-size, 16px);
    line-height: var(--type-body-md-line-height, 20px); /* 125% */
  }

  @include breakpoint(large) {
    display: none;
  }
`;

const DescriptionRepeatVisitor = styled.div`
  display: none;

  @include breakpoint(large) {
    display: flex;
    padding: var(--spacing-sm-24, 24px) var(--spacing-0, 0px);
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: var(--spacing-xs-16, 16px);
    color: var(--text-primary, #141413);
    text-align: center;
    font-family: var(--type-font-family-sans-serif, 'DM Sans');
    font-size: var(--type-body-lg-size, 20px);
    font-style: normal;
    font-weight: 400;
    line-height: var(--type-body-xl-line-height, 32px); /* 160% */
  }
`;

const DescriptionNewVisitor = styled.div`
  display: none;

  @include breakpoint(large) {
    display: flex;
    padding: var(--spacing-sm-24, 24px);
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: var(--spacing-xs-16, 16px);
  }
`;
const DescriptionTitle = styled.div`
  color: var(--text-secondary, #535366);
  text-align: center;

  font-family: var(--type-font-family-sans-serif, 'DM Sans');
  font-size: var(--type-body-lg-size, 20px);
  font-style: normal;
  font-weight: 600;
  line-height: var(--type-body-lg-line-height, 32px); /* 160% */
`;

const DescriptionList = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--spacing-xs-12, 12px);
`;

const DescriptionListItem = styled.div`
  display: flex;
  min-width: 28px;
  min-height: 28px;
  padding: 2px 0px;
  align-items: center;
  gap: var(--spacing-xs-8, 8px);
`;

const DescriptionListItemText = styled.div`
  font-family: var(--type-font-family-sans-serif, 'DM Sans');
  font-size: var(--type-body-md-size, 16px);
  font-style: normal;
  font-weight: 400;
  line-height: var(--type-body-md-line-height, 20px); /* 125% */
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const ButtonSubtitle = styled.div`
  color: var(--brand-colors-expanded-colors-turquoise-700, #3c656b);
  text-align: center;

  font-family: var(--type-font-family-sans-serif, 'DM Sans');
  font-size: var(--type-body-xs-size, 12px);
  font-style: normal;
  font-weight: 400;
  line-height: var(--type-body-xs-line-height, 16px); /* 133.333% */
`;

const PictureContainer = styled.div`
  display: flex;
  justify-content: center;

  @include breakpoint(large) {
    order: 1;
    justify-content: left;
  }
`;

const Picture = styled('div')<{ variant: string }>`
  &.variant-new_visitor {
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;

    width: 100%;
    height: calc(100vh - 496px);

    @include breakpoint(large) {
      width: 600px;
      height: 670px;
    }
  }
  &.variant-repeat_visitor {
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;

    width: 100%;
    height: calc(100vh - 566px);

    @include breakpoint(large) {
      width: 600px;
      height: 670px;
    }
  }
`;

type Advantage = {
  title: string;
  icon: React.FunctionComponent<{
    svgProps: React.SVGProps<SVGSVGElement>;
  }>;
};

type Props = {
  trialPricingCopy: string;
  paywallVariant: 'new_visitor' | 'repeat_visitor';
};

const PaywallPage = ({ trialPricingCopy, paywallVariant }: Props) => {
  const [disabled, setDisabled] = useState(false);

  const onClick = () => {
    const checkoutUrl = routes.checkout_url({
      feature: 'analytics',
      trigger: 'analytics-paywall',
      upgradeRedirect: encodeURIComponent(routes.analytics_dashboard_url({})),
    });

    trackClick({
      source: 'AnalyticsPaywallPage',
      extra: { variant: paywallVariant },
    });
    setDisabled(true);
    window.location.href = checkoutUrl;
  };
  const submitSubtitle = '1 month trial. Cancel anytime.';

  const advantages: Advantage[] = [
    {
      title: 'See the university, department, and position of your visitors',
      icon: GroupIcon,
    },
    {
      title: 'Track impact over time with detailed reports',
      icon: FinanceIcon,
    },
    {
      title: 'Get in-depth analytics for your papers and CV',
      icon: LightBulbOnIcon,
    },
  ];

  return (
    <Container>
      <InnerContainer>
        <PictureContainer>
          <Picture
            variant={paywallVariant}
            style={{
              backgroundImage: `url(${$image_url(
                '/images/analytics/paywall.png',
              )})`,
            }}
          />
        </PictureContainer>
        <MainContainer>
          <MainContent>
            <Badge>
              <LockIcon
                svgProps={{
                  width: '20px',
                  height: '20px',
                  color: 'var(--premium-gold)',
                }}
              />
              <BadgeTitle>Premium feature</BadgeTitle>
            </Badge>
            <Title>
              {paywallVariant === 'new_visitor'
                ? 'People are discovering your work. See how.'
                : 'Analytics is now part of Academia Premium'}
            </Title>
            <Subtitle>
              Track how your research is being read, cited and downloaded. All
              in one dashboard.
            </Subtitle>
            {paywallVariant === 'repeat_visitor' && (
              <DescriptionRepeatVisitor>
                To continue improving data quality, insights, and reliability,
                Analytics is now included with Academia Premium.
              </DescriptionRepeatVisitor>
            )}
            <DescriptionNewVisitor>
              <DescriptionTitle>With Premium, you can:</DescriptionTitle>
              <DescriptionList>
                {advantages.map((advantage, i) => (
                  <DescriptionListItem key={`adv-${i}`}>
                    <advantage.icon
                      svgProps={{
                        width: '24px',
                        height: '24px',
                        color: 'var(--text-link)',
                      }}
                    />
                    <DescriptionListItemText>
                      {advantage.title}
                    </DescriptionListItemText>
                  </DescriptionListItem>
                ))}
              </DescriptionList>
            </DescriptionNewVisitor>
          </MainContent>
          <ButtonContainer>
            <Button
              fullWidth
              iconRight="arrow_forward"
              size="lg"
              {...{ disabled, onClick }}
            >
              {trialPricingCopy}
            </Button>
            <ButtonSubtitle>{submitSubtitle}</ButtonSubtitle>
          </ButtonContainer>
        </MainContainer>
      </InnerContainer>
    </Container>
  );
};

export default PaywallPage;
